
import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Eye, Heart, Mail, BarChart2, TrendingUp } from 'lucide-react';
import { AdPerformance } from '@/types/ads';

interface PerformanceStatisticsProps {
  performance: AdPerformance;
}

const PerformanceStatistics: React.FC<PerformanceStatisticsProps> = ({ performance }) => {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Performance Overview</CardTitle>
          <CardDescription>
            Analytics for your promotional content
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3 mb-6">
            <div className="flex flex-col p-4 rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-blue-700 font-medium">All-time Views</span>
                <Eye className="h-4 w-4 text-blue-700" />
              </div>
              <p className="text-2xl font-bold">{performance.allTimeViews}</p>
              <div className="mt-auto pt-2 text-xs text-blue-700 flex items-center">
                <TrendingUp className="h-3 w-3 mr-1" />
                {performance.allTimeViewsChange}% increase
              </div>
            </div>
            
            <div className="flex flex-col p-4 rounded-lg bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-purple-700 font-medium">Total Likes</span>
                <Heart className="h-4 w-4 text-purple-700" />
              </div>
              <p className="text-2xl font-bold">{performance.allTimeLikes}</p>
              <div className="mt-auto pt-2 text-xs text-purple-700 flex items-center">
                <TrendingUp className="h-3 w-3 mr-1" />
                {performance.allTimeLikesChange}% increase
              </div>
            </div>
            
            <div className="flex flex-col p-4 rounded-lg bg-gradient-to-br from-green-50 to-green-100 border border-green-200">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-green-700 font-medium">Total Inquiries</span>
                <Mail className="h-4 w-4 text-green-700" />
              </div>
              <p className="text-2xl font-bold">{performance.allTimeInquiries}</p>
              <div className="mt-auto pt-2 text-xs text-green-700 flex items-center">
                <TrendingUp className="h-3 w-3 mr-1" />
                {performance.allTimeInquiriesChange}% increase
              </div>
            </div>
          </div>
          
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ad Type</TableHead>
                <TableHead className="text-right">Views</TableHead>
                <TableHead className="text-right">Likes</TableHead>
                <TableHead className="text-right">Inquiries</TableHead>
                <TableHead className="text-right">Conversion Rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {performance.byType.map((item) => (
                <TableRow key={item.type}>
                  <TableCell className="font-medium">
                    {item.type === 'story' && 'Featured Story'}
                    {item.type === 'beforeAfter' && 'Before/After Images'}
                    {item.type === 'homepage' && 'Homepage Spotlight'}
                  </TableCell>
                  <TableCell className="text-right">{item.views}</TableCell>
                  <TableCell className="text-right">{item.likes || '-'}</TableCell>
                  <TableCell className="text-right">{item.inquiries}</TableCell>
                  <TableCell className="text-right">{item.conversionRate}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
        <CardFooter>
          <p className="text-sm text-muted-foreground">
            Data updated as of {performance.lastUpdated}
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

export default PerformanceStatistics;
